import { childrenNamedInLabel, normalizeChildren } from "./familyChildren";

/** Chat copy for async flyer / screenshot capture. */

export const CAPTURE_CHAT_TEXT = {
  userSentPhoto: "📷 Foto gesendet",
  userPastedMail: "✉️ Mail eingefügt",
  pasteAck: "Mail erhalten — ich lese Termine und Fristen daraus.",
  pasteNoDates:
    "Kein Termin im Text — evtl. fehlt ein Anhang. Weiterleiten oder Seite fotografieren.",
  pasteAlreadySaved: "Das habe ich schon — nichts Neues zum Bestätigen.",
  pasteInfoOnly:
    "Keine Fristen erkannt — nur Infos in der Mail. Für Termine: weiterleiten oder Flyer fotografieren.",
  uploadAck:
    "Foto erhalten — ich werte es aus und melde mich gleich. Du kannst weiter chatten.",
  processing: "Werte das Foto noch aus…",
  confirmLead: "So habe ich es erkannt — bitte kurz prüfen:",
  confirmLowConfidence:
    "Ich bin mir nicht ganz sicher — wähle, was ich merken soll:",
  savedOne: "Termin gemerkt.",
  savedMany: (n) => `${n} Termine gemerkt.`,
  savedForwardHint:
    "Ich erinnere dich. Die nächsten Eltern-Mails reichen per Weiterleitung, ohne Foto.",
  saveError: "Speichern hat nicht geklappt — versuch es nochmal.",
  extractError:
    "Auswertung fehlgeschlagen — sag mir den Termin, dann merke ich ihn.",
  loginRequired: "Melde dich an, damit ich Flyer und Screenshots auswerten kann.",
  noneSelectable:
    "Kein Datum erkannt — sag mir den Termin, dann merke ich ihn.",
  forWhom: "Für wen?",
  alreadySaved: "Schon gemerkt.",
  noDate: "Kein Datum erkannt — bitte manuell merken.",
  cancelled: "Alles klar — nichts gemerkt.",
};

/**
 * @param {object[]} events
 */
export function buildCaptureConfirmMessage(events) {
  const low = (events || []).some((e) => e.confidence === "low");
  if (low) return CAPTURE_CHAT_TEXT.confirmLowConfidence;
  return CAPTURE_CHAT_TEXT.confirmLead;
}

/**
 * @param {number} count
 */
export function buildCaptureSavedMessage(count) {
  const lead =
    count <= 1 ? CAPTURE_CHAT_TEXT.savedOne : CAPTURE_CHAT_TEXT.savedMany(count);
  if (count < 1) return lead;
  return `${lead} ${CAPTURE_CHAT_TEXT.savedForwardHint}`;
}

/**
 * @param {object[]} events from API
 */
/** Named household children, in profile order. */
export function namedChildrenForCapture(children) {
  return normalizeChildren(children).filter((child) => child.name);
}

/**
 * Child chips for the confirm card. Shown only with two or more first names.
 * One named child stays selected. No name, or more than one, starts with none.
 * @param {object[]} items
 * @param {unknown} children
 */
export function attachChildChoices(items, children) {
  const named = namedChildrenForCapture(children);
  if (named.length < 2) return items || [];
  const choices = named.map((child) => ({ id: child.id, name: child.name }));
  return (items || []).map((item) => {
    const hits = childrenNamedInLabel(item.child, named);
    const selectedChildIds = hits.length === 1 ? [hits[0].id] : [];
    return { ...item, childChoices: choices, selectedChildIds };
  });
}

/**
 * Child label to store. One chip → that name. Several → "Emma & Luca".
 * None selected stays unset; Merken waits until the parent picks a child.
 * @param {object} item
 */
export function childLabelFromChoices(item) {
  const choices = item?.childChoices;
  if (!Array.isArray(choices) || choices.length < 2) return item?.child || null;
  const selected = new Set(item.selectedChildIds || []);
  const names = choices.filter((choice) => selected.has(choice.id)).map((choice) => choice.name);
  if (names.length === 0) return null;
  if (names.length === 1) return names[0];
  return names.join(" & ");
}

/**
 * A checked row with child chips still needs a tap before Merken.
 * @param {object} item
 */
export function captureRowNeedsChild(item) {
  return Boolean(
    item?.selected &&
      item?.selectable &&
      Array.isArray(item.childChoices) &&
      item.childChoices.length >= 2 &&
      (item.selectedChildIds || []).length === 0,
  );
}

const CONFIRM_HINT_MAX = 90;

function foldHint(text) {
  return String(text || "")
    .toLowerCase()
    .replace(/ß/g, "ss")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * One line from the flyer quote, shown under the date when it adds
 * something the title does not already say.
 * @param {object} item
 * @returns {string}
 */
export function captureConfirmHint(item) {
  const quote = String(item?.source_quote || "").replace(/\s+/g, " ").trim();
  const title = String(item?.title || "").replace(/\s+/g, " ").trim();
  if (quote.length < 8) return "";
  if (title && foldHint(quote) === foldHint(title)) return "";
  let hint = quote;
  if (title && foldHint(quote).startsWith(foldHint(title))) {
    const escaped = title.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    hint = quote.replace(new RegExp(`^${escaped}[\\s:–—\\-.,;„"']*`, "i"), "").trim();
  }
  const place = String(item?.place || "").replace(/\s+/g, " ").trim();
  if (!hint || hint.length < 8) return "";
  if (title && foldHint(hint) === foldHint(title)) return "";
  if (place && foldHint(hint) === foldHint(place)) return "";
  if (hint.length <= CONFIRM_HINT_MAX) return hint;
  return `${hint.slice(0, CONFIRM_HINT_MAX - 1).trimEnd()}…`;
}

export function captureEventsToPickerItems(events) {
  return (events || []).map((event) => ({
    eventIndex: event.eventIndex,
    title: event.title || "",
    date: event.date || "",
    end_date: event.end_date && event.end_date !== event.date ? event.end_date : null,
    time: event.time || "",
    all_day: event.all_day !== false && !event.time,
    place: event.place || "",
    action_type: event.action_type === "task" ? "task" : "event",
    remind: event.action_type === "task",
    confidence: event.confidence || "low",
    selectable: event.selectable !== false && !!event.date,
    selected: event.selectable !== false && !!event.date,
    child: event.child || null,
    source_quote: event.source_quote || "",
    applies_to: Array.isArray(event.applies_to) ? event.applies_to : [],
    event_kind: event.event_kind || null,
    duplicate: !!event.duplicate,
  }));
}

/**
 * Footer when every row is locked. A future date that is already in the plan
 * is not a missing date.
 * @param {object[]} items
 * @returns {string}
 */
export function captureConfirmBlockedHint(items) {
  const list = items || [];
  if (list.some((item) => item.selectable)) return "";
  if (list.length === 0) return "";
  if (list.some((item) => item.date)) return CAPTURE_CHAT_TEXT.alreadySaved;
  return CAPTURE_CHAT_TEXT.noDate;
}

/**
 * Confirmed photo rows as attention items for Für wen / audience follow-up.
 * @param {object[]} items schedule-items API rows
 */
export function captureAttentionItemsFromSaved(items) {
  return (items || [])
    .filter((item) => item.origin === "capture" || item.sourceKind === "capture")
    .map((item) => ({
      extractionId: item.sourceId || null,
      eventIndex: Number.isInteger(item.sourceIndex) ? item.sourceIndex : null,
      sourceKind: "capture",
      savedId: item.id,
      title: item.title || "",
      date: item.date || "",
      time: item.time || null,
      all_day: item.all_day,
      action_type: item.action_type || "event",
      event_kind: item.event_kind || null,
      child: item.child || null,
      applies_to: Array.isArray(item.applies_to) ? item.applies_to : [],
    }));
}

/**
 * @param {object[]} items picker items
 */
export function pickerItemsToConfirmBody(items) {
  return {
    items: (items || []).map((item) => ({
      eventIndex: item.eventIndex,
      selected: item.selected !== false,
      title: item.title,
      date: item.date,
      time: item.all_day === false ? item.time || null : null,
      all_day: item.all_day !== false,
      place: item.place || null,
      action_type: item.action_type,
      remind: !!item.remind,
      child: childLabelFromChoices(item),
      applies_to: Array.isArray(item.applies_to) ? item.applies_to : [],
      event_kind: item.event_kind || null,
    })),
  };
}
